import React, { useState, useEffect } from 'react';
import { 
  format, 
  addDays, 
  startOfDay, 
  subDays, 
  isWithinInterval, 
  isToday, 
  isBefore, 
  isAfter,
  addMonths,
  subMonths,
  startOfMonth,
  isSameMonth,
  getDay,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  parseISO,
  isValid
} from 'date-fns';
import { toZonedTime, format as formatTz } from 'date-fns-tz';
import './editor.css';
import { shouldShowDebugFeatures } from '../utils/environmentUtils';

// More comprehensive timezone list with DST variations
const TIMEZONES = [
  // North America
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'America/Phoenix', label: 'Arizona (No DST)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'America/Adak', label: 'Hawaii-Aleutian Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (No DST)' },
  { value: 'America/Halifax', label: 'Atlantic Time' },
  { value: 'America/St_Johns', label: 'Newfoundland Time' },
  
  // Europe
  { value: 'Europe/London', label: 'UK Time (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European Time' },
  { value: 'Europe/Helsinki', label: 'Eastern European Time' },
  { value: 'Europe/Moscow', label: 'Moscow Time (No DST)' },
  
  // Asia & Pacific
  { value: 'Asia/Tokyo', label: 'Japan Time (No DST)' },
  { value: 'Asia/Shanghai', label: 'China Time (No DST)' },
  { value: 'Asia/Kolkata', label: 'India Time (No DST)' },
  { value: 'Asia/Dubai', label: 'Gulf Time (No DST)' },
  { value: 'Asia/Singapore', label: 'Singapore Time (No DST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time' },
  { value: 'Australia/Adelaide', label: 'Australian Central Time' },
  { value: 'Australia/Perth', label: 'Australian Western Time' },
  { value: 'Pacific/Auckland', label: 'New Zealand Time' },
  
  // South America
  { value: 'America/Sao_Paulo', label: 'Brazil Time' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina Time' },
  
  // Africa
  { value: 'Africa/Cairo', label: 'Egypt Time' },
  { value: 'Africa/Johannesburg', label: 'South Africa Time (No DST)' },
  
  // UTC
  { value: 'UTC', label: 'Coordinated Universal Time (UTC)' },
];

interface Campaign {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
  type: 'promotion' | 'content' | 'rcf_planner';
  notes: string;
  rcf_data?: {
    promotionDays: number;
    contentDays: number;
  };
}

type CampaignStatus = 'active' | 'upcoming' | 'completed';

interface CalendarProps {
  campaigns: Campaign[];
  selectedTimezone: string;
  onDateSelect?: (date: Date) => void;
  onCampaignClick?: (campaign: Campaign) => void;
  onTimezoneChange?: (timezone: string) => void;
}

// Debug features check now imported from environmentUtils

export const Calendar: React.FC<CalendarProps> = ({ 
  campaigns, 
  selectedTimezone,
  onDateSelect,
  onCampaignClick,
  onTimezoneChange 
}) => {
  const [currentDate, setCurrentDate] = useState(startOfMonth(new Date()));
  const today = startOfDay(new Date());
  const minDate = new Date(2025, 0, 1); // January 1, 2025 (historical limit)
  const maxDate = addMonths(today, 13); // 13 months from today (future limit)
  
  // Calculate 35 days (5 weeks) starting from the current month
  const getCalendarDays = (date: Date) => {
    const monthStart = startOfMonth(date);
    const startDay = getDay(monthStart); // 0-6, where 0 is Sunday
    
    // Create array of days for the current month view
    const days: Date[] = [];
    
    // Start from the appropriate Sunday (might be in previous month)
    const startDate = subDays(monthStart, startDay);
    
    // Add 35 days (5 weeks) from the start date
    for (let i = 0; i < 35; i++) {
      days.push(addDays(startDate, i));
    }
    
    return days;
  };
  
  const calendarDays = getCalendarDays(currentDate);
  const currentMonth = format(currentDate, 'MMMM yyyy');
  
  // Functions to handle month navigation
  const handlePreviousMonth = () => {
    const newDate = subMonths(currentDate, 1);
    // Don't go before Jan 2025
    if (!isBefore(newDate, minDate)) {
      setCurrentDate(newDate);
    }
  };

  const handleNextMonth = () => {
    const newDate = addMonths(currentDate, 1);
    // Don't go past 13 months from today
    if (!isAfter(newDate, maxDate)) {
      setCurrentDate(newDate);
    }
  };
  
  const handleToday = () => {
    setCurrentDate(startOfMonth(today));
  };
  
  // Function to check if a date is within the allowed range
  const isDateInAllowedRange = (date: Date) => {
    return !isBefore(date, minDate) && !isAfter(date, maxDate);
  };
  
  // Helper function to get timezone display name with DST
  const getTimezoneDisplayName = (timezone: string): string => {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short'
      });
      
      // Extract the timezone abbreviation (e.g., EDT, PDT)
      const parts = formatter.formatToParts(now);
      const tzPart = parts.find(part => part.type === 'timeZoneName');
      const tzAbbr = tzPart ? tzPart.value : '';
      
      // Find the base label from our timezone list
      const tzInfo = TIMEZONES.find(tz => tz.value === timezone);
      const baseLabel = tzInfo ? tzInfo.label : timezone;
      
      // Return the label with the current abbreviation
      return `${baseLabel} (${tzAbbr})`;
    } catch (error) {
      // Keep console.error for actual errors that affect functionality
      console.error('Error getting timezone display name:', error);
      return timezone;
    }
  };

  // Initialize with user's local timezone - ONLY IF PROP IS EMPTY and callback exists
  useEffect(() => {
    try {
      // Only attempt detection if the passed-in timezone is empty/default
      if (!selectedTimezone || selectedTimezone === 'UTC') { 
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        console.log('Calendar', 'Detected initial timezone:', timezone);
        // Use the callback to set the timezone in the parent
        if (onTimezoneChange) {
          onTimezoneChange(timezone);
        }
      }
    } catch (error) {
      // Keep console.error for actual errors that affect functionality
      console.error('Failed to detect timezone:', error);
      // If detection fails and prop is empty, maybe inform parent to use UTC?
      if ((!selectedTimezone || selectedTimezone === 'UTC') && onTimezoneChange) {
         onTimezoneChange('UTC'); // Default to UTC if detection fails
      }
    }
    // Run only once on mount if selectedTimezone prop is initially empty
  }, [onTimezoneChange]); // Dependency removed: selectedTimezone

  // Special debug for May 1 specifically
  useEffect(() => {
    // Only run in development mode - keeping this check for extra safety
    if (!shouldShowDebugFeatures()) return;
    
    // Find RCF campaigns
    const rcfCampaigns = campaigns.filter(c => c.type === 'rcf_planner');
    
    if (rcfCampaigns.length === 0) {
      console.log('Calendar', 'No RCF campaigns found for debugging');
      return;
    }
    
    console.log('Calendar', '========== SPECIAL DEBUG ==========');
    console.log('Calendar', 'Debugging May 1, 2025 specifically');
    
    // Create a test date for May 1, 2025
    const may1 = new Date(2025, 4, 1); // May 1, 2025
    may1.setHours(0, 0, 0, 0);
    
    // Test each RCF campaign
    rcfCampaigns.forEach(campaign => {
      console.log('Calendar', `Testing campaign: ${campaign.name} (${campaign.id})`);
      console.log('Calendar', 'Campaign dates:', {
        startDate: new Date(campaign.startDate).toISOString(),
        endDate: new Date(campaign.endDate).toISOString()
      });
      
      // Normalize campaign start date
      const campaignStart = new Date(campaign.startDate);
      campaignStart.setHours(0, 0, 0, 0);
      
      // Check if May 1 falls within the campaign
      const campaignEnd = new Date(campaign.endDate);
      campaignEnd.setHours(0, 0, 0, 0);
      
      const isMay1InCampaign = may1 >= campaignStart && may1 <= campaignEnd;
      
      console.log('Calendar', `Is May 1 in campaign? ${isMay1InCampaign}`);
      
      if (isMay1InCampaign) {
        // Calculate day number from start (0-based index)
        const timeDiff = may1.getTime() - campaignStart.getTime();
        const dayNumber = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        
        // Get content days value
        const contentDaysValue = campaign.rcf_data?.contentDays || 0;
        
        // The first N days (where N = contentDaysValue) are content days
        const isContentDay = dayNumber >= 0 && dayNumber < contentDaysValue;
        
        console.log('Calendar', 'May 1 analysis:', {
          may1ISO: may1.toISOString(),
          campaignStartISO: campaignStart.toISOString(),
          dayNumber: dayNumber,
          contentDaysValue: contentDaysValue,
          isContentDay: isContentDay,
          result: isContentDay ? 'CONTENT (orange)' : 'PROMOTION (green)'
        });
      }
    });
    
    console.log('Calendar', '======== END SPECIAL DEBUG ========');
  }, [campaigns]); // Run when campaigns change

  const getCampaignForDate = (date: Date) => {
    // Add some detailed logging to help debug the issue
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    
    // Only debug log when debug features are enabled
    const shouldDebugMay1 = shouldShowDebugFeatures() && 
      normalizedDate.getDate() === 1 && 
      normalizedDate.getMonth() === 4 && 
      normalizedDate.getFullYear() === 2025;
    
    // Debug specific dates
    if (shouldDebugMay1) {
      console.log('Calendar', '***** May 1, 2025 campaign lookup *****');
    }
    
    // Find campaign that contains this date
    const matchingCampaign = campaigns.find(campaign => {
      const startDate = new Date(campaign.startDate);
      const endDate = new Date(campaign.endDate);
      
      // Normalize dates
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      // Check if the date is within range
      const isInRange = normalizedDate >= startDate && normalizedDate <= endDate;
      
      // Log for debugging May 1 specifically
      if (shouldDebugMay1) {
        console.log('Calendar', 'Campaign check:', {
          campaignName: campaign.name,
          campaignId: campaign.id,
          dateToCheck: normalizedDate.toISOString(),
          campaignStart: startDate.toISOString(),
          campaignEnd: endDate.toISOString(),
          isInRange: isInRange
        });
      }
      
      return isInRange;
    });
    
    if (shouldDebugMay1) {
      console.log('Calendar', 'Matching campaign:', matchingCampaign ? matchingCampaign.name : 'None');
      console.log('Calendar', '***** End May 1 campaign lookup *****');
    }
    
    return matchingCampaign;
  };

  const getCampaignStatus = (campaign: Campaign): CampaignStatus => {
    const now = new Date();
    if (isWithinInterval(now, { start: campaign.startDate, end: campaign.endDate })) {
      return 'active';
    } else if (isBefore(now, campaign.startDate)) {
      return 'upcoming';
    } else {
      return 'completed';
    }
  };

  const getBackgroundColor = (date: Date) => {
    const campaign = getCampaignForDate(date);
    if (!campaign) return '';
    
    const status = getCampaignStatus(campaign);
    const isTypePromotion = campaign.type === 'promotion';
    const isTypeCombo = campaign.type === 'rcf_planner';
    
    // For rcf_planner type, determine if this is a promotion day or content day
    if (isTypeCombo) {
      // Check if rcf_data exists
      if (!campaign.rcf_data) {
        if (shouldShowDebugFeatures()) {
          console.log('Calendar', 'RCF Planner campaign is missing rcf_data:', campaign);
        }
        return 'bg-purple-100'; // Fallback color to make it obvious something is wrong
      }
      
      const contentDaysValue = campaign.rcf_data.contentDays || 0;
      
      // May 1 specific debugging
      const isMay1 = shouldShowDebugFeatures() && 
        date.getDate() === 1 && 
        date.getMonth() === 4 && 
        date.getFullYear() === 2025;
        
      if (isMay1) {
        console.log('Calendar', '*** May 1 Color Decision ***');
        console.log('Calendar', 'Campaign:', campaign.name);
        console.log('Calendar', 'Content Days:', contentDaysValue);
      }
      
      // If there are no content days, everything is promotion days
      if (contentDaysValue === 0) {
        if (isMay1) console.log('Calendar', 'No content days, showing as PROMOTION (green)');
        if (status === 'active') return 'bg-green-100';
        else if (status === 'upcoming') return 'bg-green-50';
        else return 'bg-green-50/50'; // completed
      }
      
      // Normalize dates for comparison
      const campaignStart = new Date(campaign.startDate);
      campaignStart.setHours(0, 0, 0, 0);
      
      const dateToCheck = new Date(date);
      dateToCheck.setHours(0, 0, 0, 0);
      
      // Calculate day number from start (0-based index)
      const timeDiff = dateToCheck.getTime() - campaignStart.getTime();
      const dayNumber = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      
      // The first N days (where N = contentDaysValue) are content days
      const isContentDay = dayNumber >= 0 && dayNumber < contentDaysValue;
      
      if (isMay1) {
        console.log('Calendar', 'Day number from start:', dayNumber);
        console.log('Calendar', 'Is Content Day?', isContentDay);
        console.log('Calendar', '*** End May 1 Color Decision ***');
      }
      
      // Use the same colors as regular promotion and content campaigns for consistency
      if (status === 'active') {
        return isContentDay ? 'bg-orange-100' : 'bg-green-100';
      } else if (status === 'upcoming') {
        return isContentDay ? 'bg-orange-50' : 'bg-green-50';
      } else { // completed
        return isContentDay ? 'bg-orange-50/50' : 'bg-green-50/50';
      }
    }
    
    // For regular campaigns, use existing logic
    if (status === 'active') {
      if (isTypeCombo) return 'bg-indigo-100';
      return isTypePromotion ? 'bg-green-100' : 'bg-orange-100';
    } else if (status === 'upcoming') {
      if (isTypeCombo) return 'bg-indigo-50';
      return isTypePromotion ? 'bg-green-50' : 'bg-orange-50';
    } else { // completed
      if (isTypeCombo) return 'bg-indigo-50/50';
      return isTypePromotion ? 'bg-green-50/50' : 'bg-orange-50/50';
    }
  };

  const getStatusIndicator = (date: Date) => {
    const campaign = getCampaignForDate(date);
    if (!campaign) return null;
    
    const status = getCampaignStatus(campaign);
    const isStartDate = campaign.startDate.toDateString() === date.toDateString();
    const isEndDate = campaign.endDate.toDateString() === date.toDateString();
    
    if (status === 'active') {
      return (
        <div className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></div>
      );
    } else if (status === 'upcoming') {
      return (
        <div className="absolute top-1 right-1 w-2 h-2 bg-purple-500 rounded-full"></div>
      );
    } else { // completed
      return (
        <div className="absolute top-1 right-1 w-2 h-2 bg-gray-400 rounded-full"></div>
      );
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-semibold text-gray-900">{currentMonth}</h2>
          <div className="relative z-10 timezone-dropdown modal-affected-element">
            <select
              value={selectedTimezone || 'UTC'}
              onChange={(e) => {
                const newTimezone = e.target.value;
                console.log('Calendar', 'Timezone selection changed:', newTimezone);
                if (onTimezoneChange && newTimezone !== selectedTimezone) {
                  onTimezoneChange(newTimezone);
                }
              }}
              className="block w-full rounded-md border-gray-300 py-1.5 pl-3 pr-10 text-gray-900 shadow-sm focus:border-[#1E3B8C] focus:outline-none focus:ring-0 sm:text-sm cursor-pointer hover:bg-gray-50"
            >
              {TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>
                  {getTimezoneDisplayName(tz.value)}
                </option>
              ))}
              {selectedTimezone && !TIMEZONES.some(tz => tz.value === selectedTimezone) && (
                <option value={selectedTimezone}>{getTimezoneDisplayName(selectedTimezone)}</option>
              )}
            </select>
          </div>
        </div>
        <div className="flex space-x-2 justify-center sm:justify-end">
          <button
            onClick={handleToday}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 active:bg-gray-400 transition-colors focus:outline-none focus:ring-0 border-0 hover:border-0 focus:border-0"
            disabled={isSameMonth(today, currentDate)}
          >
            Today
          </button>
          <button
            onClick={handlePreviousMonth}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 active:bg-gray-400 transition-colors focus:outline-none focus:ring-0 border-0 hover:border-0 focus:border-0"
            disabled={isBefore(subMonths(currentDate, 1), minDate)}
          >
            Previous
          </button>
          <button
            onClick={handleNextMonth}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 active:bg-gray-400 transition-colors focus:outline-none focus:ring-0 border-0 hover:border-0 focus:border-0"
            disabled={isAfter(addMonths(currentDate, 1), maxDate)}
          >
            Next
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div key={day} className="text-center font-semibold p-2 bg-gray-100 rounded text-gray-700">
            {day}
          </div>
        ))}
        {calendarDays.map((date, index) => {
          const campaign = getCampaignForDate(date);
          const isCurrentDay = isToday(date);
          const isCurrentMonth = isSameMonth(date, currentDate);
          
          return (
            <div
              key={date.toISOString()}
              className={`aspect-square border relative ${isCurrentDay ? 'border-[#1E3B8C] border-2' : 'border-gray-200'} p-2 hover:bg-gray-50 cursor-pointer rounded flex flex-col items-center justify-center ${getBackgroundColor(date)} ${!isCurrentMonth ? 'opacity-40' : ''}`}
              onClick={() => {
                // Only allow interactions within the allowed range
                if (isDateInAllowedRange(date)) {
                  // Check if there's a campaign on this date
                  if (campaign && onCampaignClick) {
                    // If there's a campaign and we have a campaign click handler, edit the campaign
                    onCampaignClick(campaign);
                  } else if (onDateSelect) {
                    // If there's no campaign, create a new one on this date
                    onDateSelect(date);
                  }
                }
              }}
            >
              {getStatusIndicator(date)}
              <div className={`text-lg font-medium ${isCurrentDay ? 'text-[#1E3B8C]' : isCurrentMonth ? 'text-gray-900' : 'text-gray-500'}`}>
                {format(date, 'd')}
              </div>
              {campaign && (
                <div className="text-xs text-gray-600 mt-1 text-center truncate w-full">
                  {campaign.name}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <h3 className="text-sm font-medium text-gray-700">Status Legend:</h3>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-500 rounded-full mr-1.5"></div>
          <span className="text-sm text-gray-600">Active</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-purple-500 rounded-full mr-1.5"></div>
          <span className="text-sm text-gray-600">Upcoming</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gray-400 rounded-full mr-1.5"></div>
          <span className="text-sm text-gray-600">Completed</span>
        </div>
      </div>
      
      {/* Debug info in development mode */}
      {shouldShowDebugFeatures() && (
        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
          <h3 className="text-lg font-semibold mb-2 text-gray-900">RCF Campaign Diagnostics</h3>
          <div className="space-y-2">
            {campaigns.filter(c => c.type === 'rcf_planner').length === 0 ? (
              <p className="text-red-600">No RCF Planner campaigns found!</p>
            ) : (
              campaigns.filter(c => c.type === 'rcf_planner').map(c => (
                <div key={c.id} className="p-3 bg-white rounded border border-gray-300">
                  <p className="text-gray-800"><strong className="text-gray-900">Name:</strong> {c.name}</p>
                  <p className="text-gray-800"><strong className="text-gray-900">Type:</strong> {c.type}</p>
                  <p className="text-gray-800"><strong className="text-gray-900">Date Range:</strong> {new Date(c.startDate).toLocaleDateString()} to {new Date(c.endDate).toLocaleDateString()}</p>
                  <p className="text-gray-800"><strong className="text-gray-900">Has rcf_data:</strong> {c.rcf_data ? 'Yes' : 'No'}</p>
                  {c.rcf_data ? (
                    <>
                      <p className="text-gray-800"><strong className="text-gray-900">Content Days:</strong> {c.rcf_data.contentDays || 0}</p>
                      <p className="text-gray-800"><strong className="text-gray-900">Promotion Days:</strong> {c.rcf_data.promotionDays || 0}</p>
                      <p className="text-gray-800"><strong className="text-gray-900">Total Campaign Duration:</strong> {(c.rcf_data?.contentDays || 0) + (c.rcf_data?.promotionDays || 0)} days</p>
                      <p className="text-gray-800"><strong className="text-gray-900">Actual Date Difference:</strong> {Math.floor((new Date(c.endDate).getTime() - new Date(c.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1} days</p>
                      <div className="mt-2 p-2 bg-gray-50 rounded">
                        <p className="text-sm font-medium text-gray-900">Campaign Structure:</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Array.from({ length: (c.rcf_data?.contentDays || 0) + (c.rcf_data?.promotionDays || 0) }, (_, i) => {
                            const dayDate = new Date(c.startDate);
                            dayDate.setDate(dayDate.getDate() + i);
                            
                            // Get content days value
                            const contentDaysValue = c.rcf_data?.contentDays || 0;
                            
                            // The first N days (where N = contentDaysValue) are content days
                            const isContentDay = i < contentDaysValue;
                            
                            return (
                              <div 
                                key={i} 
                                className={`px-2 py-1 text-xs rounded ${isContentDay ? 'bg-orange-100 text-orange-900' : 'bg-green-100 text-green-900'}`}
                                title={dayDate.toLocaleDateString()}
                              >
                                {isContentDay ? 'C' : 'P'}{i+1}
                              </div>
                            );
                          })}
                        </div>
                        <p className="mt-2 text-xs text-gray-700">Note: When content days = 0, all days (including start date) are shown as promotion days.</p>
                      </div>
                      
                      {/* Extended Debug Section */}
                      <div className="mt-4 p-2 bg-gray-50 rounded border border-gray-300">
                        <p className="text-sm font-medium mb-2 text-gray-900">Debug: Date Testing for May 1-5, 2025</p>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-xs">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="p-1 border text-gray-900">Day</th>
                                <th className="p-1 border text-gray-900">Date</th>
                                <th className="p-1 border text-gray-900">ISO String</th>
                                <th className="p-1 border text-gray-900">Day Number</th>
                                <th className="p-1 border text-gray-900">Expected Type</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Array.from({ length: 5 }, (_, i) => {
                                const testDate = new Date(2025, 4, 1 + i); // May 1-5, 2025
                                testDate.setHours(0, 0, 0, 0);
                                
                                const campaignStart = new Date(c.startDate);
                                campaignStart.setHours(0, 0, 0, 0);
                                
                                // Calculate day number from start (0-based index)
                                const timeDiff = testDate.getTime() - campaignStart.getTime();
                                const dayNumber = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
                                
                                // Get content days value
                                const contentDaysValue = c.rcf_data?.contentDays || 0;
                                
                                // The first N days (where N = contentDaysValue) are content days
                                const isContentDay = dayNumber >= 0 && dayNumber < contentDaysValue;
                                
                                return (
                                  <tr key={i} className="border-t">
                                    <td className="p-1 border text-gray-800">{i + 1}</td>
                                    <td className="p-1 border text-gray-800">{testDate.toLocaleDateString()}</td>
                                    <td className="p-1 border text-gray-800">{testDate.toISOString()}</td>
                                    <td className="p-1 border text-gray-800">{dayNumber} {dayNumber === 0 && '(start)'}</td>
                                    <td className={`p-1 border ${isContentDay ? 'bg-orange-100 text-orange-900' : 'bg-green-100 text-green-900'}`}>
                                      {isContentDay ? 'Content' : 'Promotion'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-red-600">Missing rcf_data property! This campaign needs to be recreated.</p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}; 